import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import IHealthRiskAssessment, { IHealthRiskAssessmentCreateOrUpdate, IHealthRiskAssessmentInitResult } from "models/sql/IHealthRiskAssessment";
import HealthRiskAssessmentservice from "services/HealthRiskAssessmentService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import IFireStation, { IFireStationFilters } from "models/sql/IFireStation";
import FireStationService from "services/FireStationService";
import { formatDate } from "utilities/StringUtility";
import { addNewLogEntry, generateLog, getBaseDomain } from "utilities/MiscUtilities";
import IFireDepartment, { IFireDepartmentFilters } from "models/sql/IFireDepartment";
import FireDepartmentService from "services/FireDepartmentService";
import CountryChooser from "components/CountryChooser";
import ISalesRep, { ISalesRepFilters } from "models/sql/ISalesRep";
import SalesRepService from "services/SalesRepService";
import MiscService from "services/MiscService";
import { useBeforeUnload } from "utilities/UIUtilities";
import DPSEntitySelector from "components/DPSEntitySelector";
import IDealer, { IDealerFilters } from "models/sql/IDealer";
import DealerService from "services/DealerService";
import IOpportunity from "models/sql/IOpportunity";
import OpportunityService from "services/OpportunityService";
import DetailFooter from "components/DetailFooter";

interface IHealthRiskAssessmentsDetailProps {
    baseData?: IHealthRiskAssessmentInitResult;
}

const HealthRiskAssessmentsDetail = (props: IHealthRiskAssessmentsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [fireStation, setFireStation] = useState<IFireStation>();
    const [salesRep, setSalesRep] = useState<ISalesRep>();
    const [dealer, setDealer] = useState<IDealer>();
    const [opportunity, setOpportunity] = useState<IOpportunity>();
    const [fireDepartment, setFireDepartment] = useState<IFireDepartment>();
    const [healthRiskAssessment, setHealthRiskAssessment] = useState<IHealthRiskAssessment>({
        healthRiskAssessmentId: -1,
        stationId: undefined,
        fireDepartmentId: undefined,
        answersJson: undefined,
        stationLocationFactorScore: undefined,
        stationPpegearScore: undefined,
        stationCommonAreasScore: undefined,
        apparatusVehicleFactorsScore: undefined,
        apparatusVehicleDeconScore: undefined,
        apparatusPpegearScore: undefined,
        incidentEquipmentHandlingScore: undefined,
        incidentProtectiveEquipmentScore: undefined,
        incidentGrossDeconScore: undefined,
        teamRiskAwarenessScore: undefined,
        teamPovuseScore: undefined,
        teamHealthWellnessScore: undefined,
        zipCode: undefined,
        departmentName: undefined,
        stationNumber: undefined,
        submissionDate: undefined,
        region: undefined,
        country: undefined,
        city: undefined,
        yearBuilt: undefined,
        systemDataJson: "",
        userEmail: "",
        userName: "",
        salesRepId: undefined,
        code: "",
        source: "",
        dealerId: undefined,
        shift: ""
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const shifts = ["1st / A", "2nd / B", "3rd / C", "4th / D"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await HealthRiskAssessmentservice.GetAsync(accessToken, id);

                    if (result.record) {
                        setHealthRiskAssessment(result.record);

                        if (result.record.stationId !== null && result.record.stationId !== undefined) {
                            const stationResult = await FireStationService.GetAsync(accessToken, result.record.stationId);

                            if (stationResult.record) {
                                setFireStation(stationResult.record);
                            }
                        }

                        if (result.record.fireDepartmentId !== null && result.record.fireDepartmentId !== undefined) {
                            const departmentResult = await FireDepartmentService.GetAsync(accessToken, result.record.fireDepartmentId);

                            if (departmentResult.record) {
                                setFireDepartment(departmentResult.record);
                            }
                        }

                        if (result.record.salesRepId !== null && result.record.salesRepId !== undefined) {
                            const salesRepResult = await SalesRepService.GetAsync(accessToken, result.record.salesRepId);

                            if (salesRepResult.record) {
                                setSalesRep(salesRepResult.record);
                            }
                        }

                        if (result.record.dealerId !== null && result.record.dealerId !== undefined) {
                            const dealerResult = await DealerService.GetAsync(accessToken, result.record.dealerId);

                            if (dealerResult.record) {
                                setDealer(dealerResult.record);
                            }
                        }

                        const opportunityResult =
                            await OpportunityService.GetAllAsync(
                                accessToken,
                                0,
                                99,
                                {
                                    hraIds: [result.record.healthRiskAssessmentId]
                                });

                        if (opportunityResult.records && opportunityResult.records.length > 0) {
                            setOpportunity(opportunityResult.records[0]);
                        }
                    }
                    else {
                        globalContext.showErrorMessage("Unable to load HealthRiskAssessment");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (healthRiskAssessment && healthRiskAssessment.healthRiskAssessmentId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await HealthRiskAssessmentservice.DeleteAsync(accessToken, healthRiskAssessment.healthRiskAssessmentId);
            globalContext.setLoading(false);
            navigate("/HealthRiskAssessments");
        }
    };

    const submitFormAsync = async (changePage = true, newHRAData: IHealthRiskAssessment | null = null) => {
        if (!newHRAData) {
            newHRAData = healthRiskAssessment;
        }
        else {
            setHealthRiskAssessment(newHRAData);
        }

        if (newHRAData) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IHealthRiskAssessmentCreateOrUpdate = {
                healthRiskAssessmentId: newHRAData.healthRiskAssessmentId,
                stationId: newHRAData.stationId,
                fireDepartmentId: newHRAData.fireDepartmentId,
                answersJson: newHRAData.answersJson,
                stationLocationFactorScore: newHRAData.stationLocationFactorScore,
                stationPpegearScore: newHRAData.stationPpegearScore,
                stationCommonAreasScore: newHRAData.stationCommonAreasScore,
                apparatusVehicleFactorsScore: newHRAData.apparatusVehicleFactorsScore,
                apparatusVehicleDeconScore: newHRAData.apparatusVehicleDeconScore,
                apparatusPpegearScore: newHRAData.apparatusPpegearScore,
                incidentEquipmentHandlingScore: newHRAData.incidentEquipmentHandlingScore,
                incidentProtectiveEquipmentScore: newHRAData.incidentProtectiveEquipmentScore,
                incidentGrossDeconScore: newHRAData.incidentGrossDeconScore,
                teamRiskAwarenessScore: newHRAData.teamRiskAwarenessScore,
                teamPovuseScore: newHRAData.teamPovuseScore,
                teamHealthWellnessScore: newHRAData.teamHealthWellnessScore,
                zipCode: newHRAData.zipCode,
                departmentName: newHRAData.departmentName,
                stationNumber: newHRAData.stationNumber,
                submissionDate: newHRAData.submissionDate,
                region: newHRAData.region,
                country: newHRAData.country,
                city: newHRAData.city,
                notes: newHRAData.notes,
                yearBuilt: newHRAData.yearBuilt,
                systemDataJson: newHRAData.systemDataJson,
                userEmail: newHRAData.userEmail,
                userName: newHRAData.userName,
                salesRepId: newHRAData.salesRepId,
                code: newHRAData.code,
                source: newHRAData.source,
                dealerId: newHRAData.dealerId,
                shift: newHRAData.shift
            };

            await HealthRiskAssessmentservice.CreateOrUpdateAsync(accessToken, data);
            setHasFormDataChanged(false);
            globalContext.setLoading(false);

            if (data.healthRiskAssessmentId === -1) {
                navigate(-1);
            }
        }
    };

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    if (healthRiskAssessment) {
        return (
            <>
                <Row>
                    <Col>
                        {
                            (healthRiskAssessment.systemDataJson ?? "").indexOf("follow up email") > -1 ?
                                <span className="ps-2" style={{ width: "100%", display: "block", textAlign: "center", backgroundColor: "#19875473", padding: "6px", fontWeight: "bold" }}><FontAwesomeIcon icon={faCheck as IconProp} /> Followed Up</span> :
                                <Button
                                    style={{ width: "100%" }}
                                    variant="success"
                                    title="Send Follow Up Email"
                                    className="quick-link"
                                    onClick={async (e) => {
                                        if (!healthRiskAssessment.userEmail) {
                                            window.alert("This hra must first have a user email address before you can automatically follow up.");
                                            return;
                                        }

                                        e.stopPropagation();
                                        e.preventDefault();
                                        globalContext.showModal({
                                            content:
                                                <></>,
                                            title: "Send Follow Up Email",
                                            formElements: [
                                                {
                                                    key: "title",
                                                    type: "text",
                                                    title: `Email Title`,
                                                    value: "Regarding your Health Risk Assessment"
                                                },
                                                {
                                                    key: "body",
                                                    type: "textarea",
                                                    title: `Body`,
                                                    style: { minHeight: "200px" },
                                                    value: `We recently received your Health Risk Assessment and would like to discuss your results. <a href=\"${window.location.protocol}//${getBaseDomain()}/Public/HRAReport?code=${healthRiskAssessment.code}\">View your results</a>`
                                                }
                                            ],
                                            okCallback:
                                                async (data) => {
                                                    var emailAddress = healthRiskAssessment.userEmail;
                                                    var fromEmailAddress = globalContext.userEmail;
                                                    var title = data?.[0].value ?? "";
                                                    var body = data?.[1].value ?? "";

                                                    if (emailAddress) {
                                                        const accessToken = await globalContext.getAccessTokenAsync();

                                                        await MiscService.sendEmailRawAsync(
                                                            accessToken,
                                                            emailAddress,
                                                            fromEmailAddress,
                                                            title,
                                                            body);

                                                        await submitFormAsync(
                                                            false,
                                                            addNewLogEntry(
                                                                (new Date()),
                                                                `${globalContext.userName} has sent a follow up email to '${healthRiskAssessment.userEmail}'`,
                                                                globalContext.userEmail,
                                                                101,
                                                                healthRiskAssessment));

                                                        window.alert("Your follow up email has been sent to " + emailAddress + ". Please save before leaving the page.");
                                                    }
                                                },
                                            cancelCallback: () => { },
                                            customActionButton: {
                                                title: "Manually Contacted",
                                                buttonType: "success",
                                                onClick: async () => {
                                                    await submitFormAsync(
                                                        false,
                                                        addNewLogEntry(
                                                            (new Date()),
                                                            `${globalContext.userName} manually sent a follow up email to '${healthRiskAssessment.userEmail}'`,
                                                            globalContext.userEmail,
                                                            101,
                                                            healthRiskAssessment));

                                                    window.alert("Your manual follow up has been noted.");
                                                },
                                                closesModal: true
                                            }
                                        });
                                    }}>
                                    <FontAwesomeIcon icon={faEnvelope as IconProp} /> Send Follow Up Email
                                </Button>
                        }
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <>
                                            <strong>{formatDate(healthRiskAssessment.submissionDate)}</strong><br />
                                            <a href={`https://${getBaseDomain()}/Public/HRAReport?code=${healthRiskAssessment.code}`} target="_blank">View Report</a>
                                            {
                                                opportunity &&
                                                <>
                                                    <a className="ps-2" href={`/dps/Opportunities/Detail/${opportunity.opportunityId}`} target="_blank">View Lead / Opportunity</a>
                                                </>
                                            }
                                        </>
                                    }
                                </Card.Title>

                                <DPSEntitySelector<IFireStation, IFireStationFilters>
                                    entityFilterSearchPropName={"postalCodes"}
                                    entityService={FireStationService}
                                    entityTypeName="Station"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireStation}
                                    generateEntityTitle={(entry) => `${entry.name} (${entry.number})`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Stations/Detail/${entry.fireStationId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setHealthRiskAssessment({ ...healthRiskAssessment, stationId: newEntry?.fireStationId || undefined });
                                        setFireStation(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IFireDepartment, IFireDepartmentFilters>
                                    entityFilterSearchPropName={"fireDeptNames"}
                                    entityService={FireDepartmentService}
                                    entityTypeName="Department"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={fireDepartment}
                                    generateEntityTitle={(entry) => `${entry.fireDeptName} ${entry.hqcity} ${entry.hqstate}, ${entry.hqzip}`}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Departments/Detail/${entry.fireDepartmentId}`);
                                        }
                                    }}
                                    onClickAddNewHandler={() => {
                                        window.open(`/DPS/Departments/Detail/?defs={"fireDeptName":"${healthRiskAssessment.departmentName ?? ""}","hqstate":"","hqCountry":"${healthRiskAssessment.country ?? ""}"}`);
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setHealthRiskAssessment({ ...healthRiskAssessment, fireDepartmentId: newEntry?.fireDepartmentId || undefined });
                                        setFireDepartment(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<IDealer, IDealerFilters>
                                    entityFilterSearchPropName={"dealerNames"}
                                    entityService={DealerService}
                                    entityTypeName="Dealer"
                                    defaultEntityFilter={{}}
                                    selectedEntityRef={dealer}
                                    generateEntityTitle={(entry) => entry.dealerName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Dealers/Detail/${entry.dealerId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setHealthRiskAssessment({ ...healthRiskAssessment, dealerId: newEntry?.dealerId || undefined });
                                        setDealer(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <DPSEntitySelector<ISalesRep, ISalesRepFilters>
                                    entityFilterSearchPropName={"names"}
                                    entityService={SalesRepService}
                                    entityTypeName="Rep"
                                    defaultEntityFilter={{ dealerIds: dealer ? [dealer.dealerId] : undefined }}
                                    selectedEntityRef={salesRep}
                                    generateEntityTitle={(entry) => entry?.fullName ?? ""}
                                    onClickExternalHandler={(entry) => {
                                        if (entry) {
                                            window.open(`/DPS/Reps/Detail/${entry.salesRepId}`);
                                        }
                                    }}
                                    entityChangeHandler={(newEntry) => {
                                        setHealthRiskAssessment({ ...healthRiskAssessment, salesRepId: newEntry?.salesRepId || undefined });
                                        setSalesRep(newEntry);
                                        setHasFormDataChanged(true);
                                    }}
                                />

                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Country</Form.Label>
                                            {
                                                healthRiskAssessment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {healthRiskAssessment.fireDepartment.hqCountry})<br />
                                                </>
                                            }
                                            <CountryChooser
                                                onChange={newVal => {
                                                    setHealthRiskAssessment({ ...healthRiskAssessment, country: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                defaultValue={healthRiskAssessment.country}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>State (From Dept)</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={true}
                                                    value={healthRiskAssessment?.fireDepartment?.hqstate ?? ""}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>City</Form.Label>
                                            {
                                                healthRiskAssessment.fireDepartment &&
                                                <>
                                                    &nbsp;(Dept: {healthRiskAssessment.fireDepartment.hqcity})<br />
                                                </>
                                            }
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, city: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.city}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Year Built</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, yearBuilt: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.yearBuilt || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                {/*
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Apparatus PPE Gear Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, apparatusPpegearScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.apparatusPpegearScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Apparatus Vehicle Decon Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, apparatusVehicleDeconScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.apparatusVehicleDeconScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Apparatus Vehicle Factors Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, apparatusVehicleFactorsScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.apparatusVehicleFactorsScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Incident Equipment Handling Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, incidentEquipmentHandlingScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.incidentEquipmentHandlingScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Incident Gross Decon Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, incidentGrossDeconScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.incidentGrossDeconScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Incident Protective Equipment Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, incidentProtectiveEquipmentScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.incidentProtectiveEquipmentScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Station Common Areas Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, stationCommonAreasScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.stationCommonAreasScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Station Location Factor Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, stationLocationFactorScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.stationLocationFactorScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Station PPE Gear Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, stationPpegearScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.stationPpegearScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Team Health Wellness Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, teamHealthWellnessScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.teamHealthWellnessScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Team POV Use Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, teamPovuseScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.teamPovuseScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Team Risk Awareness Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, teamRiskAwarenessScore: parseInt(e.target.value || "0") });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.teamRiskAwarenessScore || 0}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                */}
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>ZIP Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, zipCode: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.zipCode}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Department Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, departmentName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.departmentName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Station Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, stationNumber: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.stationNumber}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Shift</Form.Label>
                                            {
                                                <Form.Select
                                                    onChange={async (e) => {
                                                        setHealthRiskAssessment({ ...healthRiskAssessment, shift: e.target.value });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                    value={healthRiskAssessment.shift}>
                                                    <option value={""}>All</option>
                                                    {
                                                        shifts.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                    }
                                                </Form.Select>
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Submitter Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, userName: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.userName}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col className={fireDepartment && (!fireDepartment.emailSuffix || (fireDepartment.emailSuffix && healthRiskAssessment.userEmail?.toLocaleLowerCase().indexOf(fireDepartment.emailSuffix) == -1)) ? "col-danger" : ""}>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Submitter Email</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, userEmail: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.userEmail}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Code</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, code: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.code}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Source</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={!globalContext.roles.some(r => r === "Web.Admin")}
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setHealthRiskAssessment({ ...healthRiskAssessment, source: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={healthRiskAssessment.source}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Score</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    readOnly={true}
                                                    value={calcScore(healthRiskAssessment)}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <Row className="align-items-end">
                        <Col>
                            <Card className="mb-2">
                                <Card.Body>
                                    <Card.Title>
                                        <strong>Log</strong><br />
                                    </Card.Title>
                                    {generateLog<IHealthRiskAssessment>(healthRiskAssessment)}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                }

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Assessment, please try again.</h1>
            </>
        );
    }
}

export default HealthRiskAssessmentsDetail;
